import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useMemo, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import useLocalization from '../../../../hooks/useLocalization';
import useProductAvailability from '../../../../hooks/useProductAvailability';
import { useSearchNavigation } from '../../../../hooks/useSearchNavigation';
import { usePrefetchManager } from '../../../../queries/prefetch/usePrefetchManager';
import { styles } from './Header.styles';

type BottomNavItem = {
  key: string;
  label: string;
  isActive: boolean;
  icon: ReactNode;
  testId?: string;
  to?: string;
  onPrefetch?: () => void;
};

type MobileBottomNavProps = {
  showSearchEntry: boolean;
  isDrawerOpen: boolean;
  toggleDrawer: () => void;
};

const MobileBottomNav = ({ showSearchEntry, isDrawerOpen, toggleDrawer }: MobileBottomNavProps) => {
  const { pathname } = useLocation();
  const { t } = useLocalization();
  const { hasSportsbookAccess, hasCasinoAccess } = useProductAvailability();
  const { isAuthenticated } = useAuth();
  const { prefetchSportsLanding, prefetchMyBets } = usePrefetchManager();

  const searchLabel = t('search').replace('...', '');
  const menuItem = useMemo<BottomNavItem>(
    () => ({
      key: 'menu',
      label: t('menu', 'Menu'),
      icon: <MenuRoundedIcon fontSize='small' />,
      isActive: isDrawerOpen,
    }),
    [isDrawerOpen, t]
  );
  const searchItem = useMemo<BottomNavItem>(
    () => ({
      key: 'search',
      label: searchLabel,
      icon: <SearchRoundedIcon fontSize='small' />,
      isActive: pathname.startsWith('/search'),
    }),
    [pathname, searchLabel]
  );

  const trailingItems = useMemo<BottomNavItem[]>(() => {
    const trailingItems: BottomNavItem[] = [];

    if (hasCasinoAccess) {
      trailingItems.push({
        key: 'casino',
        label: t('casino'),
        icon: <CasinoRoundedIcon />,
        isActive: pathname.startsWith('/casino'),
        to: '/casino',
      });
    }

    if (hasSportsbookAccess) {
      trailingItems.push({
        key: 'sports',
        label: t('sports'),
        icon: <SportsSoccerRoundedIcon />,
        isActive: pathname.startsWith('/sports') || pathname.startsWith('/in-play'),
        to: '/sports',
        onPrefetch: prefetchSportsLanding,
      });
    }

    if (isAuthenticated) {
      trailingItems.push({
        key: 'my-bets',
        label: t('betslips', 'Betslips'),
        icon: <ReceiptLongRoundedIcon fontSize='small' />,
        isActive: pathname.startsWith('/my-bets'),
        to: '/my-bets',
        onPrefetch: prefetchMyBets,
        testId: 'neobet-mobile-my-bets-nav',
      });
    }

    return trailingItems;
  }, [hasCasinoAccess, hasSportsbookAccess, isAuthenticated, pathname, prefetchMyBets, prefetchSportsLanding, t]);

  return (
    <Box sx={styles.mobileBottomNav} data-testid='neobet-mobile-bottom-nav'>
      <MobileBottomNavButton item={menuItem} onClick={toggleDrawer} />
      {showSearchEntry && <MobileBottomNavSearchItem item={searchItem} />}
      {trailingItems.map((item) => (
        <MobileBottomNavButton key={item.key} item={item} />
      ))}
    </Box>
  );
};

const MobileBottomNavItemContent = ({ item }: { item: BottomNavItem }) => (
  <Stack
    spacing={0.5}
    sx={{
      alignItems: 'center',
    }}
  >
    <Box sx={styles.bottomNavIconWrap(item.isActive)}>{item.icon}</Box>
    <Typography component='span' sx={styles.bottomNavLabel}>
      {item.label}
    </Typography>
  </Stack>
);

type MobileBottomNavButtonProps = {
  item: BottomNavItem;
  onClick?: () => void;
};

const MobileBottomNavButton = ({ item, onClick }: MobileBottomNavButtonProps) => {
  if (item.to) {
    return (
      <ButtonBase
        component={RouterLink}
        to={item.to}
        sx={styles.bottomNavItem}
        data-testid={item.testId}
        onPointerEnter={item.onPrefetch}
        onFocus={item.onPrefetch}
      >
        <MobileBottomNavItemContent item={item} />
      </ButtonBase>
    );
  }

  return (
    <ButtonBase sx={styles.bottomNavItem} data-testid={item.testId} onClick={onClick}>
      <MobileBottomNavItemContent item={item} />
    </ButtonBase>
  );
};

type MobileBottomNavSearchItemProps = {
  item: BottomNavItem;
};

const MobileBottomNavSearchItem = ({ item }: MobileBottomNavSearchItemProps) => {
  const { handleSearchClick } = useSearchNavigation();

  return <MobileBottomNavButton item={item} onClick={handleSearchClick} />;
};

export default MobileBottomNav;
